import { Box, Button, ButtonProps, IconButton, IconButtonProps, Stack, styled, Typography } from '@mui/material';
import { packagesModel } from 'entities/mine-package';
import { selectCurrentPackage, selectPackagesList } from 'entities/mine-package/model';
import {
  COLOR_ACCENT,
  COLOR_BG_ALPHA_8,
  COLOR_BG_BLACK_2,
  COLOR_BG_SECONDARY,
  COLOR_SECONDARY,
  COLOR_STROKE,
  Icons,
  useAppDispatch,
  useShallowSelector,
  useStepper,
} from 'shared';
import { NetworkGuardedButton } from 'shared/ui/buttons';
import { NumberFormat } from 'shared/ui/number-format';

import { STEPPER_ROUTES } from '../../lib/stepper';
import { BackgroundCard, EarnCalculator, warningSvg } from '../shared';

import { PackagesPreviewSlider } from './package-slider';

const StyledIconButton = styled(IconButton)<IconButtonProps>({
  borderRadius: '50%',
  backgroundColor: COLOR_BG_ALPHA_8,
  border: `1px solid ${COLOR_STROKE}`,
  '&:disabled': {
    backgroundColor: COLOR_BG_ALPHA_8,
    opacity: 0.5,
  },
  '&:hover': {
    borderColor: COLOR_ACCENT,
    backgroundColor: COLOR_BG_ALPHA_8,
    '& svg': {
      color: COLOR_ACCENT,
    },
  },
});

const BuyPackageButton = ({ children, ...props }: ButtonProps) => {
  return (
    <Button fullWidth size="medium" {...props} disabled>
      <Typography className="button black medium">{children}</Typography>
    </Button>
  );
  // return (
  //   <NetworkGuardedButton fullWidth>
  //     <Button fullWidth size="medium" {...props}>
  //       <Typography className="button black medium">{children}</Typography>
  //     </Button>
  //   </NetworkGuardedButton>
  // );
};

export const SelectPackageView = () => {
  const dispatch = useAppDispatch();
  const packages = useShallowSelector(selectPackagesList);
  const currentPackage = useShallowSelector(selectCurrentPackage);

  const { next } = useStepper();

  const { id: currentPackageId, price, units } = currentPackage;

  const nextPackage = () => {
    if (currentPackageId < packages.length - 1) {
      dispatch(packagesModel.actions.setPackage(currentPackageId + 1));
    }
  };

  const prevPackage = () => {
    if (currentPackageId > 0) {
      dispatch(packagesModel.actions.setPackage(currentPackageId - 1));
    }
  };

  const handlePackageChange = (idx: number) => {
    if (currentPackageId !== idx) {
      dispatch(packagesModel.actions.setPackage(idx));
    }
  };

  return (
    <BackgroundCard>
      <Stack direction="row" gap={{ xs: 1, sm: 5 }} alignItems="center" width="100%" mb={3}>
        <StyledIconButton onClick={prevPackage} disabled={currentPackageId === 0}>
          <Icons.ArrowChevron sx={{ rotate: '180deg', color: COLOR_SECONDARY }} />
        </StyledIconButton>
        <PackagesPreviewSlider
          packages={packages}
          selectedPackage={currentPackageId}
          onPackageChange={handlePackageChange}
        />
        <StyledIconButton onClick={nextPackage} disabled={currentPackageId === packages.length - 1}>
          <Icons.ArrowChevron sx={{ color: COLOR_SECONDARY }} />
        </StyledIconButton>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        bgcolor={COLOR_BG_SECONDARY}
        borderRadius={3}
        gap={1.5}
        py={1.5}
        pl={1.5}
        pr={3.5}
        mb={2}
      >
        <Box
          minWidth={32}
          minHeight={32}
          bgcolor={COLOR_BG_BLACK_2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="50%"
        >
          <Box component="img" alt="warning" src={warningSvg} width={16} />
        </Box>
        <Typography variant="body2" className="primary">
          Select wisely; users are limited to a single Mining Rig Package.
        </Typography>
      </Stack>

      <EarnCalculator tap2minePackage={currentPackage} sx={{ mb: 2 }} />

      <BuyPackageButton disabled={!units.available} onClick={() => next(STEPPER_ROUTES.PAYMENT_SELECT_METHOD)}>
        {!units.available ? (
          'No items available'
        ) : (
          <>
            Buy for{' '}
            <NumberFormat prefix="$" decimals={0} withComas variant="inherit">
              {price}
            </NumberFormat>
          </>
        )}
      </BuyPackageButton>
    </BackgroundCard>
  );
};
